import { useState } from "react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { Home, ChevronDown, Settings as SettingsIcon, AlertTriangle, Star, Activity as ActivityIcon, Menu, X } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useTeamUsage } from "@/hooks/useTeamUsage";
import { Badge } from "./ui/badge";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const { planLimits, usage, getUsagePercentage, loading: usageLoading } = useTeamUsage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get initial data synchronously from session and localStorage cache
  const getInitialData = () => {
    if (isImpersonating && impersonatedUser) {
      const cachedAvatar = localStorage.getItem(`avatar_${impersonatedUser.id}`);
      return {
        userName: impersonatedUser.name.split(' ')[0],
        avatarUrl: cachedAvatar || null,
      };
    }

    try {
      const sessionStr = localStorage.getItem('sb-' + supabase.supabaseUrl.split('//')[1].split('.')[0] + '-auth-token');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        const user = session?.user;
        if (user) {
          const userName = user.user_metadata?.full_name?.split(' ')[0] ||
                          user.email?.split('@')[0];
          if (userName) {
            const cachedAvatar = localStorage.getItem(`avatar_${user.id}`);
            return { userName, avatarUrl: cachedAvatar || null };
          }
        }
      }
    } catch (e) {
      // Ignore localStorage errors
    }

    return null;
  };

  const { data: userProfile } = useQuery({
    queryKey: ['header-user-profile', isImpersonating, impersonatedUser?.id],
    queryFn: async () => {
      if (isImpersonating && impersonatedUser) {
        const effectiveUserId = await getEffectiveUserId();
        if (!effectiveUserId) return null;

        const { data: profile } = await supabase
          .from('profiles')
          .select('avatar_url, full_name')
          .eq('user_id', effectiveUserId)
          .maybeSingle();

        const avatarUrl = profile?.avatar_url || null;
        if (avatarUrl) {
          localStorage.setItem(`avatar_${effectiveUserId}`, avatarUrl);
        } else {
          localStorage.removeItem(`avatar_${effectiveUserId}`);
        }

        return {
          userName: impersonatedUser.name.split(' ')[0],
          avatarUrl,
        };
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;

      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return null;

      const [profileResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('avatar_url, full_name')
          .eq('user_id', effectiveUserId)
          .maybeSingle(),
      ]);

      const profile = profileResult.data;
      const userName = profile?.full_name?.split(' ')[0] ||
                      session.user.user_metadata?.full_name?.split(' ')[0] ||
                      session.user.email?.split('@')[0] ||
                      'User';

      const avatarUrl = profile?.avatar_url || null;
      if (avatarUrl) {
        localStorage.setItem(`avatar_${effectiveUserId}`, avatarUrl);
      } else {
        localStorage.removeItem(`avatar_${effectiveUserId}`);
      }

      return { userName, avatarUrl };
    },
    placeholderData: getInitialData,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
  });

  const userName = userProfile?.userName;
  const avatarUrl = userProfile?.avatarUrl || null;

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await supabase.auth.signOut();
    toast({ title: "Logged out successfully" });
    navigate("/login");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinks = [
    { to: "/dashboard", icon: <Home className="h-4 w-4" />, label: "Home" },
    { to: "/spotlight/manage", icon: <Star className="h-4 w-4" />, label: "Spotlight" },
    { to: "/activity", icon: <ActivityIcon className="h-4 w-4" />, label: "Activity" },
    { to: "/settings", icon: <SettingsIcon className="h-4 w-4" />, label: "Settings" },
  ];

  const avatarDropdown = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={avatarUrl || undefined} alt={userName || ''} />
            <AvatarFallback className="bg-accent text-accent-foreground text-xs">
              {userName?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="hidden md:inline">{userName}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings?tab=billing" className="cursor-pointer">Billing</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Loading skeleton — same structure for both mobile and desktop
  if (!userName) {
    return (
      <header className="border-b border-border bg-card">
        {/* Mobile skeleton */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <div className="w-8 h-8" />
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <div className="w-8 h-8" />
        </div>
        {/* Desktop skeleton */}
        <div className="hidden md:flex container mx-auto px-6 py-4 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard"><Logo size="sm" /></Link>
            <nav className="flex items-center gap-1">
              {navLinks.map(({ to, icon, label }) => (
                <Button key={to} variant="ghost" size="sm" asChild>
                  <Link to={to}>{icon}{label}</Link>
                </Button>
              ))}
            </nav>
          </div>
          <div />
        </div>
      </header>
    );
  }

  return (
    <header className="border-b border-border bg-card">
      {/* ── Mobile bar ── */}
      <div className="md:hidden flex items-center justify-between px-4 py-3">
        {/* Hamburger */}
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label="Toggle navigation menu"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>

        {/* Logo — centered */}
        <Link to="/dashboard" onClick={closeMobileMenu}>
          <Logo size="sm" />
        </Link>

        {/* Avatar dropdown */}
        <div className="flex items-center gap-1">
          {!usageLoading && usage && planLimits && planLimits.max_active_norths !== null && getUsagePercentage('norths') >= 80 && (
            <Link to="/settings?tab=billing">
              <Badge
                variant={getUsagePercentage('norths') >= 100 ? "destructive" : "default"}
                className="gap-1 cursor-pointer hover:opacity-80 text-xs px-1.5"
              >
                <AlertTriangle className="h-3 w-3" />
              </Badge>
            </Link>
          )}
          <NotificationBell />
          {avatarDropdown}
        </div>
      </div>

      {/* ── Mobile slide-down nav drawer ── */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-card">
          <nav className="flex flex-col py-2">
            {navLinks.map(({ to, icon, label }) => {
              const isActive = location.pathname === to || (to === "/dashboard" && location.pathname === "/");
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors hover:bg-muted ${
                    isActive ? "text-primary bg-muted/50" : "text-foreground"
                  }`}
                >
                  {icon}
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* ── Desktop bar (unchanged) ── */}
      <div className="hidden md:flex container mx-auto px-6 py-4 items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/dashboard">
            <Logo size="sm" />
          </Link>
          <nav className="flex items-center gap-1">
            {navLinks.map(({ to, icon, label }) => (
              <Button key={to} variant="ghost" size="sm" asChild>
                <Link to={to}>{icon}{label}</Link>
              </Button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {!usageLoading && usage && planLimits && planLimits.max_active_norths !== null && getUsagePercentage('norths') >= 80 && (
            <Link to="/settings?tab=billing">
              <Badge
                variant={getUsagePercentage('norths') >= 100 ? "destructive" : "default"}
                className="gap-1 cursor-pointer hover:opacity-80"
              >
                <AlertTriangle className="h-3 w-3" />
                {getUsagePercentage('norths') >= 100
                  ? 'Limit Reached'
                  : `${Math.round(getUsagePercentage('norths'))}% Used`
                }
              </Badge>
            </Link>
          )}
          <NotificationBell />
          {avatarDropdown}
        </div>
      </div>
    </header>
  );
};
